import Phaser from 'phaser';
import { loadGodMode, loadEnemyTypeScales, loadEnemyAnchors, loadEnemyTypeStatOverrides, applyDefaultProfileIfEmpty } from '@/config/GodMode';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { CampaignOverrideStore } from '@/systems/CampaignOverrideStore';
import { loadComponentOverrides } from '@/config/ComponentOverrides';
import { ResourceManager } from '@/systems/ResourceManager';
import { loadAnimDefs, registerSavedAnimations } from '@/scenes/AnimationPreviewScene';
import { loadCinematicsFromJson } from '@/cinematic/CinematicDirector';
import { loadCinematicState } from '@/cinematic/CinematicState';
import { loadTemplatesFromJSON } from '@/config/RobotTemplates';
import spritesheetManifest from 'virtual:spritesheet-manifest';
import { EnemyType, ENEMY_SPRITE_FILES } from '@/config/GameConfig';

// ── Loading screen layout constants ──
const HIVE_X = 300;
const GRUNT_X = 900;
const CHAR_Y = 390;
const GRUNT_Y = 390;   // grunt lower to align feet with larger hive
const GROUND_Y = 350;
const BAR_X = 350;
const BAR_Y = 480;
const BAR_W = 500;
const BAR_H = 24;
const ACID_GREEN = 0x66ff22;
const SPRITE_SCALE = 0.35;
const HIVE_SCALE = 1.0;

// ── Animation cycle timing ──
const CYCLE_DURATION = 3.0;
const FIRE_TIME = 0.2;             // when acid spawns
const FLIGHT_DURATION = 1.2;       // acid flight time
const IMPACT_TIME = FIRE_TIME + FLIGHT_DURATION; // 1.4s
const MIN_DISPLAY_TIME = 2000;     // ms — minimum loading screen visibility

export class BootScene extends Phaser.Scene {
  // Loading screen sprites
  private hiveSprite!: Phaser.GameObjects.Image;
  private gruntSprite!: Phaser.GameObjects.Image;
  private barFill!: Phaser.GameObjects.Graphics;
  private pctText!: Phaser.GameObjects.Text;

  // Acid projectile
  private acidContainer: Phaser.GameObjects.Container | null = null;
  private acidShadow: Phaser.GameObjects.Ellipse | null = null;

  // Animation state
  private crawlPhase = 0;
  private cycleTimer = 0;
  private acidActive = false;
  private acidProgress = 0;
  private hiveBaseScaleX = HIVE_SCALE;
  private hiveBaseScaleY = HIVE_SCALE;
  private gruntBaseX = GRUNT_X;

  // Load tracking
  private loadDone = false;
  private minTimeElapsed = false;
  private loadingActive = true;

  constructor() {
    super({ key: 'BootScene' });
  }

  // ═══════════════════════════════════════════════════════════
  // Phase 1: Load only the 3 sprites needed for the loading screen
  // ═══════════════════════════════════════════════════════════
  preload(): void {
    this.load.image('enemy_hive', '/ennemies/Hive.png');
    this.load.image('enemy_grunt', '/ennemies/grunt.png');
    this.load.spritesheet('acid', '/assets/acid_4x1.png', {
      frameWidth: 256,
      frameHeight: 177,
    });
  }

  // ═══════════════════════════════════════════════════════════
  // Phase 2: Show animated loading screen, load everything else
  // ═══════════════════════════════════════════════════════════
  create(): void {
    this.cameras.main.setBackgroundColor('#ffffff');
    this.buildLoadingScreen();
    this.createAcidAnimation();

    // Queue all remaining game assets
    this.queueAllAssets();

    // Progress tracking
    this.load.on('progress', (value: number) => {
      this.updateProgressBar(value);
    });
    this.load.on('complete', () => {
      this.loadDone = true;
      if (this.minTimeElapsed) this.finishLoading();
    });
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[BootScene] Failed to load: ${file.key} (${file.url})`);
    });

    // Minimum display time
    this.time.delayedCall(MIN_DISPLAY_TIME, () => {
      this.minTimeElapsed = true;
      if (this.loadDone) this.finishLoading();
    });

    // Start loading phase 2
    this.load.start();
  }

  // ═══════════════════════════════════════════════════════════
  // Update: animate the loading screen
  // ═══════════════════════════════════════════════════════════
  update(_time: number, delta: number): void {
    if (!this.loadingActive) return;
    const dt = delta / 1000;

    this.updateHiveCrawl(dt);
    this.updateCycle(dt);
  }

  // ── Loading screen construction ──


  private buildLoadingScreen(): void {
    const cx = 600;

    // Ground line
    const ground = this.add.graphics();
    ground.lineStyle(1, 0x334466, 0.4);
    ground.lineBetween(150, GROUND_Y, 1050, GROUND_Y);

    // Hive sprite (left)
    this.hiveSprite = this.add.image(HIVE_X, CHAR_Y, 'enemy_hive')
      .setScale(HIVE_SCALE)
      .setOrigin(0.5, 1);

    // Grunt sprite (right)
    this.gruntSprite = this.add.image(GRUNT_X, GRUNT_Y, 'enemy_grunt')
      .setScale(SPRITE_SCALE)
      .setOrigin(0.5, 1)
      .setFlipX(true); // face left toward Hive

    // "LOADING..." text
    this.add.text(cx, BAR_Y - 30, 'LOADING...', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#66ff22',
    }).setOrigin(0.5);

    // Progress bar background
    const barBg = this.add.graphics();
    barBg.fillStyle(0x111122, 0.8);
    barBg.fillRoundedRect(BAR_X, BAR_Y, BAR_W, BAR_H, 6);

    // Progress bar fill (updated dynamically)
    this.barFill = this.add.graphics();

    // Progress bar border
    const barBorder = this.add.graphics();
    barBorder.lineStyle(2, 0x334466, 1);
    barBorder.strokeRoundedRect(BAR_X, BAR_Y, BAR_W, BAR_H, 6);

    // Percentage text
    this.pctText = this.add.text(cx, BAR_Y + BAR_H + 14, '0%', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#aaaaaa',
    }).setOrigin(0.5);
  }

  private createAcidAnimation(): void {
    if (!this.anims.exists('acid_fly')) {
      this.anims.create({
        key: 'acid_fly',
        frames: this.anims.generateFrameNumbers('acid', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: -1,
      });
    }
  }

  private updateProgressBar(value: number): void {
    this.barFill.clear();
    const fillW = (BAR_W - 4) * Math.min(value, 1);
    if (fillW > 0) {
      this.barFill.fillStyle(ACID_GREEN, 1);
      this.barFill.fillRoundedRect(BAR_X + 2, BAR_Y + 2, fillW, BAR_H - 4, 4);
    }
    this.pctText.setText(`${Math.floor(value * 100)}%`);
  }

  // ── Hive crawl oscillation (continuous) ──

  private updateHiveCrawl(dt: number): void {
    this.crawlPhase += dt * 5;
    const distortX = 1 + 0.08 * Math.sin(this.crawlPhase);
    const distortY = 1 - 0.04 * Math.sin(this.crawlPhase);
    this.hiveSprite.setScale(
      this.hiveBaseScaleX * distortX,
      this.hiveBaseScaleY * distortY,
    );
  }

  // ── Attack cycle ──

  private updateCycle(dt: number): void {
    this.cycleTimer += dt;

    // Hive squeeze telegraph before firing
    if (this.cycleTimer >= 0 && this.cycleTimer < FIRE_TIME) {
      const squeezeT = this.cycleTimer / FIRE_TIME;
      this.hiveBaseScaleY = HIVE_SCALE * (1 - 0.1 * squeezeT);
      this.hiveBaseScaleX = HIVE_SCALE * (1 + 0.05 * squeezeT);
    }

    // Spawn acid
    if (this.cycleTimer >= FIRE_TIME && !this.acidActive) {
      this.hiveBaseScaleX = HIVE_SCALE;
      this.hiveBaseScaleY = HIVE_SCALE;
      this.spawnAcid();
    }

    // Update acid flight
    if (this.acidActive) {
      this.updateAcidFlight(dt);
    }

    // Reset cycle
    if (this.cycleTimer >= CYCLE_DURATION) {
      this.cycleTimer = 0;
    }
  }

  private spawnAcid(): void {
    this.acidActive = true;
    this.acidProgress = 0;

    const angle = Math.atan2(0, GRUNT_X - HIVE_X); // horizontal
    const container = this.add.container(HIVE_X, CHAR_Y - 20).setDepth(24);
    const acidSprite = this.add.sprite(0, 0, 'acid')
      .setScale(0.4)
      .setRotation(angle)
      .play('acid_fly');
    container.add(acidSprite);
    // Glow
    container.add(this.add.circle(0, 0, 12, ACID_GREEN, 0.15));

    this.acidContainer = container;

    // Shadow
    this.acidShadow = this.add.ellipse(HIVE_X, GROUND_Y, 10, 5, 0x000000, 0.3)
      .setDepth(5);
  }

  private updateAcidFlight(dt: number): void {
    if (!this.acidContainer || !this.acidShadow) return;

    this.acidProgress += dt / FLIGHT_DURATION;

    if (this.acidProgress >= 1) {
      // Impact
      this.onAcidImpact();
      return;
    }

    const t = this.acidProgress;
    const sx = HIVE_X;
    const sy = CHAR_Y - 20;
    const tx = GRUNT_X;
    const ty = GRUNT_Y - 20;
    const dist = tx - sx;
    const arcHeight = dist * 0.35; // slightly lower arc for loading screen

    const prevX = this.acidContainer.x;
    const prevY = this.acidContainer.y;

    const groundX = sx + (tx - sx) * t;
    const groundY = sy + (ty - sy) * t;
    const altitude = -arcHeight * Math.sin(t * Math.PI);

    this.acidContainer.setPosition(groundX, groundY + altitude);
    this.acidShadow.setPosition(groundX, GROUND_Y);

    // Rotate to follow trajectory tangent
    const mdx = this.acidContainer.x - prevX;
    const mdy = this.acidContainer.y - prevY;
    if (mdx !== 0 || mdy !== 0) {
      this.acidContainer.setRotation(Math.atan2(mdy, mdx));
    }

    // Shadow scale based on altitude
    const shadowScale = 1 - Math.abs(altitude) / arcHeight * 0.5;
    this.acidShadow.setScale(shadowScale, shadowScale * 0.5);

    // Drip particles
    if (Math.random() < 0.3) {
      const drip = this.add.circle(
        groundX + (Math.random() - 0.5) * 4,
        groundY + altitude + (Math.random() - 0.5) * 4,
        2, ACID_GREEN, 0.6,
      ).setDepth(23);
      this.tweens.add({
        targets: drip, alpha: 0, y: drip.y + 10, scaleX: 0.3, scaleY: 0.3,
        duration: 400 + Math.random() * 200, onComplete: () => drip.destroy(),
      });
    }
  }

  private onAcidImpact(): void {
    // Destroy acid projectile
    this.acidContainer?.destroy();
    this.acidContainer = null;
    this.acidShadow?.destroy();
    this.acidShadow = null;
    this.acidActive = false;

    const ix = GRUNT_X;
    const iy = GRUNT_Y - 20;

    // Expanding green ring
    const ring = this.add.circle(ix, iy, 12, ACID_GREEN, 0.6).setDepth(22);
    this.tweens.add({
      targets: ring, alpha: 0, scaleX: 3, scaleY: 3,
      duration: 400, onComplete: () => ring.destroy(),
    });

    // Acid puddle
    const puddle = this.add.ellipse(ix, GROUND_Y, 30, 12, 0x44aa11, 0.35).setDepth(5);
    this.tweens.add({
      targets: puddle, alpha: 0, duration: 1200, delay: 200,
      onComplete: () => puddle.destroy(),
    });

    // Splash particles
    for (let p = 0; p < 6; p++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * 15;
      const px = ix + Math.cos(angle) * dist;
      const py = iy + Math.sin(angle) * dist;
      const droplet = this.add.circle(px, py, 2 + Math.random() * 2, 0x88ff33, 0.8).setDepth(23);
      this.tweens.add({
        targets: droplet, alpha: 0,
        x: px + Math.cos(angle) * 15, y: py + Math.sin(angle) * 15,
        duration: 300 + Math.random() * 200, onComplete: () => droplet.destroy(),
      });
    }

    // Grunt hit reaction: red flash + recoil
    this.gruntSprite.setTint(0xff4444);
    this.tweens.add({
      targets: this.gruntSprite,
      x: this.gruntBaseX + 15,
      duration: 80,
      ease: 'Power2',
      yoyo: true,
      onComplete: () => {
        this.gruntSprite.clearTint();
        this.gruntSprite.setX(this.gruntBaseX);
      },
    });
  }

  // ── Transition ──

  private async finishLoading(): Promise<void> {
    if (!this.loadingActive) return;
    this.loadingActive = false;

    // Show 100%
    this.updateProgressBar(1);

    // Run all post-load init
    await this.runPostLoadInit();

    // Brief pause then fade out
    this.time.delayedCall(400, () => {
      this.cameras.main.fadeOut(400, 0x1a, 0x1a, 0x2e);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('SplashScene');
      });
    });
  }

  // ═══════════════════════════════════════════════════════════
  // Post-load initialization (extracted from original create())
  // ═══════════════════════════════════════════════════════════
  private async runPostLoadInit(): Promise<void> {
    // Character portraits: use LINEAR filtering instead of NEAREST (pixelArt global)
    const charKeys = [
      'char_colossus', 'char_cyberlord', 'char_gamer_kiddo', 'char_influencer',
      'char_mustang', 'char_old_mercenary', 'char_commandant',
      'char_shneiper', 'char_major', 'char_secretary', 'char_technomonk',
      'char_voice_assistant', 'frame_smartphone',
    ];
    for (const key of charKeys) {
      const tex = this.textures.get(key);
      if (tex && tex.source.length > 0) {
        tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
      }
    }

    await applyDefaultProfileIfEmpty();
    await loadTemplatesFromJSON();
    await loadCinematicsFromJson();
    await loadCinematicState();
    await CampaignOverrideStore.load();
    loadGodMode();
    loadEnemyTypeScales();
    loadEnemyAnchors();
    loadEnemyTypeStatOverrides();
    CustomMapStore.load();
    loadComponentOverrides();
    ResourceManager.load();
    this.applySavedFrameOverrides();
    registerSavedAnimations(this);
    this.createEnemyAnimations();
    this.createMudAnimations();
    this.generateProceduralTextures();
  }

  /** Register enemy walk/attack/death animations from pixelart spritesheets */
  private createEnemyAnimations(): void {
    const ANIM_CONFIGS: { suffix: string; fps: number; repeat: number }[] = [
      { suffix: 'walk',   fps: 10, repeat: -1 },
      { suffix: 'attack', fps: 12, repeat: 0 },
      { suffix: 'death',  fps: 8,  repeat: 0 },
    ];
    for (const [, info] of Object.entries(ENEMY_SPRITE_FILES)) {
      for (const cfg of ANIM_CONFIGS) {
        const file = cfg.suffix === 'walk' ? info.file
          : cfg.suffix === 'attack' ? info.attack
          : info.death;
        if (!file) continue;
        const key = `enemy_${file}_${cfg.suffix}`;
        if (!this.textures.exists(key) || this.anims.exists(key)) continue;
        this.anims.create({
          key,
          frames: this.anims.generateFrameNumbers(key, { start: 0, end: 8 }),
          frameRate: cfg.fps,
          repeat: cfg.repeat,
        });
      }
    }
  }

  /** Register mud projectile + impact animations (phase 2 assets must be loaded) */
  private createMudAnimations(): void {
    if (!this.anims.exists('mud_fly')) {
      this.anims.create({
        key: 'mud_fly',
        frames: this.anims.generateFrameNumbers('mud', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: -1,
      });
    }
    if (!this.anims.exists('mud_splat')) {
      this.anims.create({
        key: 'mud_splat',
        frames: this.anims.generateFrameNumbers('boue', { start: 0, end: 3 }),
        frameRate: 12,
        repeat: 0,
      });
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Phase 2 asset queue (everything from original preload minus the 3 loading screen assets)
  // ═══════════════════════════════════════════════════════════
  private queueAllAssets(): void {
    // Splash screen logos
    this.load.image('logo_liva', '/images/LIVA.png');
    this.load.image('logo_probably_playable', '/images/probably_playable_studio.png');

    this.load.image('title_screen', '/images/title_screen.png');
    this.load.image('icon_blocked', '/assets/blocked.png');
    this.load.image('icon_upgrade', '/assets/upgrade_button.png');
    this.load.image('icon_co', '/assets/Connardium.png');
    this.load.image('icon_electro', '/assets/electronic.png');
    this.load.image('icon_energy', '/assets/energy.png');
    this.load.image('icon_coin', '/assets/coins.png');
    this.load.image('frame_smartphone', '/assets/smartphone.png');
    this.load.image('fx_spark', '/assets/spark.png');
    this.load.image('hp_bar_frame', '/assets/HP_BAR.png');

    // ── Sprite UI assets (PixelLab pixel art, used when GOD.useSpriteUI) ──
    const uiAssets: [string, string][] = [
      ['ui_panel_large',    '/ui-experiments/panel_large.png'],
      ['ui_panel_small',    '/ui-experiments/panel_small.png'],
      ['ui_panel_wave',     '/ui-experiments/panel_wave.png'],
      ['ui_panel_upgrade',  '/ui-experiments/panel_upgrade.png'],
      ['ui_panel_frame',    '/ui-experiments/panel_frame.png'],
      ['ui_frame_square',   '/ui-experiments/frame_square.png'],
      ['ui_button_wide',    '/ui-experiments/button_wide.png'],
      ['ui_button_normal',  '/ui-experiments/button_normal.png'],
      ['ui_button_speed',   '/ui-experiments/button_speed.png'],
      ['ui_button_square',  '/ui-experiments/button_square.png'],
      ['ui_slot_component', '/ui-experiments/slot_component.png'],
      ['ui_slot_locked',    '/ui-experiments/slot_locked.png'],
      ['ui_bar_hp',         '/ui-experiments/bar_hp.png'],
      ['ui_bar_currency',   '/ui-experiments/bar_currency.png'],
      ['ui_bar_frame',      '/ui-experiments/bar_frame.png'],
      ['ui_banner_title',   '/ui-experiments/title_banner.png'],
      ['ui_banner_victory', '/ui-experiments/banner_victory.png'],
      ['ui_banner_defeat',  '/ui-experiments/banner_defeat.png'],
      ['ui_icon_frame',     '/ui-experiments/icon_frame.png'],
    ];
    for (const [key, path] of uiAssets) this.load.image(key, path);

    // Fire flame sprites for flamethrower VFX
    this.load.image('fire_0_H', '/assets/fire_0_H.png');
    this.load.image('fire_1_H', '/assets/fire_1_H.png');
    this.load.image('fire_2_H', '/assets/fire_2_H.png');
    this.load.image('fire_3_V', '/assets/fire_3_V.png');
    this.load.image('fire_4_V', '/assets/fire_4_V.png');
    this.load.image('icon_sound', '/assets/sound.png');
    this.load.image('icon_nosound', '/assets/nosound.png');
    this.load.image('icon_edit', '/assets/edit_button.png');
    this.load.image('icon_delete', '/assets/Trash_button.png');

    // Title screen videos
    this.load.video('title_video_1', '/videos/title_screen_video_1.mp4', false);
    this.load.video('title_video_1_rev', '/videos/title_screen_video_1_rev.mp4', false);
    this.load.video('title_video_2', '/videos/title_screen_video_2.mp4', false);
    this.load.video('title_video_3', '/videos/title_screen_video_3.mp4', false);
    this.load.video('title_video_4', '/videos/title_screen_video_4.mp4', false);
    this.load.video('title_video_5', '/videos/title_screen_video_5.mp4', false);

    // Actor sprites
    this.load.image('actor_player_base', '/Actors/Player_base.png');
    this.load.image('actor_enemy_base', '/Actors/ennemy_base.png');

    // Map tiles
    this.load.image('tile_gazon', '/tiles/gazon.png');
    this.load.image('tile_goudron', '/tiles/goudron.png');
    this.load.image('tile_mur', '/tiles/mur.png');
    this.load.image('tile_electro', '/tiles/electro.png');
    this.load.image('tile_metal', '/tiles/metal.png');
    this.load.image('tile_placement', '/tiles/placement.png');
    this.load.image('tile_broken_placement', '/tiles/broken_placement.png');
    this.load.image('tile_placement_led', '/tiles/placement_LED.png');
    // Auto-discovered spritesheets (_NxM.png convention)
    for (const entry of spritesheetManifest) {
      // Skip 'acid' — already loaded in phase 1
      if (this.textures.exists(entry.key)) continue;
      this.load.spritesheet(entry.key, entry.path, {
        frameWidth: entry.frameWidth,
        frameHeight: entry.frameHeight,
      });
    }
    this.load.image('tile_boue', '/tiles/Mud.png');
    this.load.image('tile_eau', '/tiles/deep_water.png');
    this.load.image('tile_grille', '/tiles/Grille.png');
    this.load.image('tile_magma', '/tiles/magma.png');

    // Foreground decorations
    this.load.image('decor_arbre', '/foreground/arbre.png');
    this.load.image('decor_ruines', '/foreground/Ruines.png');
    this.load.image('decor_colonne', '/foreground/Colonne_ruine.png');
    this.load.image('decor_gouffre', '/foreground/gouffre.png');
    this.load.image('decor_conduit', '/foreground/conduit.png');
    this.load.image('decor_barrel', '/foreground/barrel.png');
    this.load.image('decor_broken_wall', '/foreground/brokenWall.png');
    this.load.image('decor_caisse_0', '/foreground/caisse_0.png');
    this.load.image('decor_caisse_1', '/foreground/caisse_1.png');
    this.load.image('decor_caisse_2', '/foreground/caisse_2.png');
    this.load.image('decor_dead_tree', '/foreground/dead_tree.png');
    this.load.image('decor_flaque', '/foreground/flaque.png');
    this.load.image('decor_rocher', '/foreground/rocher.png');
    this.load.image('decor_souche', '/foreground/souche.png');
    this.load.image('decor_connardium', '/foreground/Connardium.png');
    this.load.image('decor_garbage', '/foreground/Garbage.png');
    this.load.image('decor_barriere', '/foreground/barriere_bois.png');
    this.load.image('decor_broken_statue', '/foreground/broken_statue.png');

    // Enemy sprites (hive + grunt already loaded in phase 1)
    this.load.image('enemy_runner', '/ennemies/runner.png');
    this.load.image('enemy_tank', '/ennemies/tank.png');
    this.load.image('enemy_swarm', '/ennemies/swarm.png');
    this.load.image('enemy_brute', '/ennemies/Brute.png');
    this.load.image('enemy_phantom', '/ennemies/Phantom.png');
    this.load.image('enemy_drone', '/ennemies/Drone.png');
    this.load.image('enemy_bomber', '/ennemies/Bomber.png');
    this.load.image('enemy_wasp', '/ennemies/Wasp.png');
    this.load.image('enemy_specter', '/ennemies/Specter.png');
    this.load.image('enemy_healer', '/ennemies/Healer.png');

    // Enemy animation spritesheets (walk/attack/death — 9 frames of 128x128)
    for (const [, info] of Object.entries(ENEMY_SPRITE_FILES)) {
      const base = info.file;
      this.load.spritesheet(`enemy_${base}_walk`, `/pixelart/ennemies/${base}_walk.png`,
        { frameWidth: 128, frameHeight: 128 });
      if (info.attack)
        this.load.spritesheet(`enemy_${info.attack}_attack`, `/pixelart/ennemies/${info.attack}_attack.png`,
          { frameWidth: 128, frameHeight: 128 });
      if (info.death)
        this.load.spritesheet(`enemy_${info.death}_death`, `/pixelart/ennemies/${info.death}_death.png`,
          { frameWidth: 128, frameHeight: 128 });
    }

    // Robot component sprites
    this.load.image('comp_structure_0', '/Robot_components/structure_0.png');
    this.load.image('comp_structure_1', '/Robot_components/structure_1.png');
    this.load.image('comp_structure_2', '/Robot_components/structure_2.png');
    this.load.image('comp_armor_0', '/Robot_components/Armor_0.png');
    this.load.image('comp_armor_1', '/Robot_components/Armor_1.png');
    this.load.image('comp_armor_3', '/Robot_components/Armor_3.png');
    this.load.image('comp_battery_0', '/Robot_components/Battery_0.png');
    this.load.image('comp_battery_1', '/Robot_components/Battery_1.png');
    this.load.image('comp_battery_2', '/Robot_components/Battery_2.png');
    this.load.image('comp_direction_0', '/Robot_components/Direction.png');
    this.load.image('comp_bracket_0', '/Robot_components/Bracket_0.png');
    this.load.image('comp_bracket_1', '/Robot_components/Bracket_1.png');
    this.load.image('comp_bracket_2', '/Robot_components/Bracket_2.png');
    this.load.image('comp_wheel_0', '/Robot_components/wheel_0.png');
    this.load.image('comp_wheel_1', '/Robot_components/Wheel_1.png');
    this.load.image('comp_wheel_2', '/Robot_components/wheel_2.png');
    this.load.image('comp_wheel_3', '/Robot_components/wheel_3.png');
    this.load.image('comp_wheel_4', '/Robot_components/wheel_4.png');
    this.load.image('comp_stab_0', '/Robot_components/stab_0.png');
    this.load.image('comp_stab_1', '/Robot_components/stab_1.png');
    this.load.image('comp_stab_2', '/Robot_components/stab_2.png');
    this.load.image('comp_thruster_0', '/Robot_components/Thruster_0.png');
    this.load.image('comp_thruster_1', '/Robot_components/Thruster_1.png');
    this.load.image('comp_thruster_3', '/Robot_components/Thruster_3.png');
    this.load.image('comp_wing_0', '/Robot_components/Wing_0.png');
    this.load.image('comp_wing_1', '/Robot_components/Wing_1.png');
    this.load.image('comp_wing_2', '/Robot_components/Wing_2.png');
    this.load.image('comp_radar_short_0', '/Robot_components/radar_short_0.png');
    this.load.image('comp_radar_short_1', '/Robot_components/radar_short_1.png');
    this.load.image('comp_radar_medium_0', '/Robot_components/radar_Medium_0.png');
    this.load.image('comp_radar_medium_1', '/Robot_components/radar_Medium_1.png');
    this.load.image('comp_repair_0', '/Robot_components/repair_0.png');
    this.load.image('comp_repair_1', '/Robot_components/repair_1.png');
    this.load.image('comp_repair_2', '/Robot_components/repair_2.png');
    this.load.image('comp_rocket_pod_0', '/Robot_components/RocketPod_0.png');
    this.load.image('comp_rocket_pod_1', '/Robot_components/RocketPod_1.png');
    this.load.image('comp_rocket_pod_2', '/Robot_components/RocketPod_2.png');
    this.load.image('comp_cannon_light_0', '/Robot_components/Canon_normal_0.png');
    this.load.image('comp_cannon_light_1', '/Robot_components/Canon_normal_1.png');
    this.load.image('comp_cannon_light_2', '/Robot_components/Canon_normal_2.png');
    this.load.image('comp_cannon_heavy_0', '/Robot_components/Canon_Heavy_0.png');
    this.load.image('comp_cannon_heavy_1', '/Robot_components/Canon_Heavy_1.png');
    this.load.image('comp_cannon_heavy_2', '/Robot_components/Canon_Heavy_2.png');
    this.load.image('comp_excavator_0', '/Robot_components/Excavator_0.png');
    this.load.image('comp_excavator_1', '/Robot_components/Excavator_1.png');
    this.load.image('comp_excavator_2', '/Robot_components/Excavator_2.png');
    this.load.image('comp_jumper_0', '/Robot_components/Jumper_0.png');
    this.load.image('comp_jumper_1', '/Robot_components/Jumper_1.png');
    this.load.image('comp_jumper_2', '/Robot_components/Jumper_2.png');
    this.load.image('comp_laser_0', '/Robot_components/Laser_0.png');
    this.load.image('comp_laser_1', '/Robot_components/Laser_1.png');
    this.load.image('comp_laser_2', '/Robot_components/Laser_2.png');
    this.load.image('comp_burst_cannon_0', '/Robot_components/Canon_burst_0.png');
    this.load.image('comp_burst_cannon_1', '/Robot_components/Canon_burst_1.png');
    this.load.image('comp_burst_cannon_2', '/Robot_components/Canon_burst_2.png');
    this.load.image('comp_aa_missile_0', '/Robot_components/AAmissiles_0.png');
    this.load.image('comp_aa_missile_1', '/Robot_components/AAmissiles_1.png');
    this.load.image('comp_aa_missile_2', '/Robot_components/AAmissiles_2.png');
    this.load.image('comp_ground_missile_0', '/Robot_components/missile_ground_0.png');
    this.load.image('comp_ground_missile_1', '/Robot_components/missile_ground_1.png');
    this.load.image('comp_ground_missile_2', '/Robot_components/missile_ground_2.png');
    this.load.image('comp_mine_layer_0', '/Robot_components/mine_0.png');
    this.load.image('comp_mine_layer_1', '/Robot_components/mine_1.png');
    this.load.image('comp_mine_layer_2', '/Robot_components/mine_2.png');
    this.load.image('comp_explosive_0', '/Robot_components/explosive_0.png');
    this.load.image('comp_explosive_1', '/Robot_components/explosive_1.png');
    this.load.image('comp_explosive_2', '/Robot_components/explosive_2.png');
    this.load.image('comp_explosive_3', '/Robot_components/explosive_3.png');
    this.load.image('comp_flamethrower_0', '/Robot_components/flamethrower_0.png');
    this.load.image('comp_flamethrower_1', '/Robot_components/flamethrower_1.png');
    this.load.image('comp_flamethrower_2', '/Robot_components/flamethrower_2.png');
    this.load.image('comp_recharge_0', '/Robot_components/Recharge_0.png');
    this.load.image('comp_recharge_1', '/Robot_components/Recharge_1.png');
    this.load.image('comp_recharge_2', '/Robot_components/Recharge_2.png');
    this.load.image('comp_mud_catapult', '/Robot_components/Mud_Catapult.png');

    // Projectile sprites
    this.load.image('proj_bullet', '/projectiles/canon_bullet.png');
    this.load.image('proj_shell', '/projectiles/Shell.png');
    this.load.image('proj_missile', '/projectiles/Ground_Missile.png');

    // Character portraits
    this.load.image('char_colossus', '/characters/Colossus.png');
    this.load.image('char_cyberlord', '/characters/CyberLord.png');
    this.load.image('char_gamer_kiddo', '/characters/Gamer_Kiddo.png');
    this.load.image('char_influencer', '/characters/InfluencerMandy.png');
    this.load.image('char_mustang', '/characters/Mustang.png');
    this.load.image('char_old_mercenary', '/characters/OldMercenary.png');
    this.load.image('char_commandant', '/characters/Purplecommandant.png');
    this.load.image('char_shneiper', '/characters/Shneiper.png');
    this.load.image('char_major', '/characters/Major.png');
    this.load.image('char_secretary', '/characters/Secretary .png');
    this.load.image('char_technomonk', '/characters/Technomonk.png');
    this.load.image('char_voice_assistant', '/characters/VoiceAssistant.png');

    // Load baked spritesheets from saved animation defs
    const manifestPaths = new Set(spritesheetManifest.map(e => e.path));
    const savedDefs = loadAnimDefs();
    for (const [key, def] of Object.entries(savedDefs)) {
      if (!def.sourceFile || !def.frameWidth || !def.frameHeight) continue;
      if (this.textures.exists(key)) continue;
      const srcPath = def.sourceFile.startsWith('/') ? def.sourceFile : `/${def.sourceFile}`;
      if (manifestPaths.has(srcPath)) continue;
      this.load.spritesheet(key, `/${def.sourceFile}`, {
        frameWidth: def.frameWidth,
        frameHeight: def.frameHeight,
      });
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Utility methods (unchanged from original)
  // ═══════════════════════════════════════════════════════════

  /** Re-slice spritesheets whose frame dimensions were customized in Animation Preview */
  private applySavedFrameOverrides(): void {
    const defs = loadAnimDefs();
    for (const [key, def] of Object.entries(defs)) {
      if (!def.frameWidth || !def.frameHeight) continue;
      const tex = this.textures.get(key);
      if (!tex || tex.key === '__MISSING') continue;

      const frame0 = tex.get(0);
      if (frame0 && (frame0.width !== def.frameWidth || frame0.height !== def.frameHeight)) {
        const source = tex.getSourceImage() as HTMLImageElement;
        this.textures.remove(key);
        this.textures.addSpriteSheet(key, source, {
          frameWidth: def.frameWidth,
          frameHeight: def.frameHeight,
        });
      }
    }
  }

  private generateProceduralTextures(): void {
    // Explosion: radial gradient circle
    const expGfx = this.make.graphics({ x: 0, y: 0 });
    const cx = 32, cy = 32;
    expGfx.fillStyle(0xff4400, 0.15);
    expGfx.fillCircle(cx, cy, 30);
    expGfx.fillStyle(0xff6600, 0.3);
    expGfx.fillCircle(cx, cy, 22);
    expGfx.fillStyle(0xffaa00, 0.5);
    expGfx.fillCircle(cx, cy, 14);
    expGfx.fillStyle(0xffdd44, 0.7);
    expGfx.fillCircle(cx, cy, 8);
    expGfx.fillStyle(0xffffff, 0.9);
    expGfx.fillCircle(cx, cy, 4);
    expGfx.generateTexture('fx_explosion', 64, 64);
    expGfx.destroy();

    // Smoke puff: soft grey circle
    const smokeGfx = this.make.graphics({ x: 0, y: 0 });
    smokeGfx.fillStyle(0x888888, 0.15);
    smokeGfx.fillCircle(16, 16, 14);
    smokeGfx.fillStyle(0xaaaaaa, 0.25);
    smokeGfx.fillCircle(16, 16, 9);
    smokeGfx.fillStyle(0xcccccc, 0.3);
    smokeGfx.fillCircle(16, 16, 5);
    smokeGfx.generateTexture('fx_smoke', 32, 32);
    smokeGfx.destroy();
  }
}
